import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';

interface CN61HtmlReportData {
  inbound?: {
    inboundE55SummarySection?: any;
    inboundTdCalculationsSection?: any;
  };
  outbound?: {
    outboundE55SummarySection?: any;
    outboundTdCalculationsSection?: any;
  };
  // Legacy support for old structure
  inboundE55SummarySection?: any;
  outboundE55SummarySection?: any;
  inboundTdCalculationsSection?: any;
  outboundTdCalculationsSection?: any;
  // Total section
  totalSection?: any;
  // Currency
  currency?: string;
  // Country information for report titles
  fromCountry?: string;
  toCountry?: string;
}

interface CN61HtmlReportTableProps {
  data: CN61HtmlReportData[];
  title?: string;
  direction?: 'inbound' | 'outbound' | 'unknown';
}

// Helper function to format row type labels
const formatRowTypeLabel = (rowType: string): string => {
  const labelMap: Record<string, string> = {
    'P_OR_S_FORMAT': 'P or S format',
    'G_FORMAT': 'G format',
    'E_FORMAT': 'E format',
    'MIXED_MAIL': 'Mixed mail',
    'NON_SAMPLED_MAIL': 'Non-sampled mail',
    'M_BAGS': 'M bags',
    'REGISTERED_ITEMS': 'Registered items',
    'INSURED_ITEMS': 'Insured items',
    'EXPRESS_ITEMS': 'Express items',
    'TOTAL': 'Total',
  };
  return labelMap[rowType] || rowType?.replace(/_/g, ' ') || '';
};

function CN61HtmlReportTable({ data, title, direction = 'unknown' }: CN61HtmlReportTableProps): React.ReactElement {
  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }} data-testid="cn61-html-report-no-data">
        <Typography variant="body1" color="text.secondary" data-testid="cn61-html-report-no-data-text">
          No data available
        </Typography>
      </Box>
    );
  }

  const reportData = data[0];

  // Background colors based on direction
  const getBackgroundColor = (isHeader: boolean = false) => {
    if (isHeader) {
      return direction === 'inbound' ? '#e8f5e8' : direction === 'outbound' ? '#ffe8e8' : '#f5f5f5';
    } else {
      return direction === 'inbound' ? '#f0f8f0' : direction === 'outbound' ? '#fff0f0' : '#fafafa';
    }
  };

  // Render E55 Summary Sections with proper numbering
  const renderE55SummarySections = () => {
    const fromCountry = reportData.fromCountry || 'Origin';
    const toCountry = reportData.toCountry || 'Destination';
    
    return (
      <Box sx={{ mb: 4 }} data-testid="cn61-html-report-e55-summary-sections">
        {/* Main E55 Summary Title */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#063142', mb: 3 }} data-testid="cn61-html-report-e55-summary-title">
          1. Annual volumes
        </Typography>
        
        {/* Inbound Section */}
        {(inboundData?.inboundE55SummarySection || reportData.inboundE55SummarySection) && 
          renderE55SummarySection(
            inboundData?.inboundE55SummarySection || reportData.inboundE55SummarySection, 
            `1.1 Inbound mail (from ${fromCountry} to ${toCountry})`, 
            'inbound'
          )}
        
        {/* Outbound Section */}
        {(outboundData?.outboundE55SummarySection || reportData.outboundE55SummarySection) && 
          renderE55SummarySection(
            outboundData?.outboundE55SummarySection || reportData.outboundE55SummarySection, 
            `1.2 Outbound mail (from ${toCountry} to ${fromCountry})`, 
            'outbound'
          )}
      </Box>
    );
  };

  // Render E55 Summary Section with unified quarterly table
  const renderE55SummarySection = (e55Data: any, sectionTitle: string, sectionDirection: 'inbound' | 'outbound' = 'inbound') => {
    if (!e55Data) return null;

    const quarters = ['q1', 'q2', 'q3', 'q4'];
    
    // Get background color for this specific section
    const getSectionBackgroundColor = (isHeader: boolean = false) => {
      if (isHeader) {
        return sectionDirection === 'inbound' ? '#e8f5e8' : sectionDirection === 'outbound' ? '#ffe8e8' : '#f5f5f5';
      } else {
        return sectionDirection === 'inbound' ? '#f0f8f0' : sectionDirection === 'outbound' ? '#fff0f0' : '#fafafa';
      }
    };
    
    return (
      <Box sx={{ mb: 4 }} data-testid={`cn61-html-report-e55-${sectionDirection}-section`}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#063142', mb: 2 }} data-testid={`cn61-html-report-e55-${sectionDirection}-title`}>
          {sectionTitle}
        </Typography>
        
        <TableContainer component={Paper} sx={{ boxShadow: 1 }} data-testid={`cn61-html-report-e55-${sectionDirection}-table-container`}>
          <Table size="small" data-testid={`cn61-html-report-e55-${sectionDirection}-table`}>
            <TableHead data-testid={`cn61-html-report-e55-${sectionDirection}-table-head`}>
              <TableRow sx={{ backgroundColor: getSectionBackgroundColor(true) }} data-testid={`cn61-html-report-e55-${sectionDirection}-table-header-row`}>
                <TableCell sx={{ fontWeight: 700, borderRight: '1px solid #ddd', verticalAlign: 'middle', textTransform: 'none !important' }} data-testid="cn61-html-report-e55-quarter-header">Quarter</TableCell>
                <TableCell sx={{ fontWeight: 700, borderRight: '1px solid #ddd', verticalAlign: 'middle', textTransform: 'none !important' }} data-testid="cn61-html-report-e55-mail-category-header">Mail Category</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center', borderRight: '1px solid #ddd', textTransform: 'none !important' }} data-testid="cn61-html-report-e55-p-or-s-format-header">P or S format</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center', borderRight: '1px solid #ddd', textTransform: 'none !important' }} data-testid="cn61-html-report-e55-g-format-header">G format</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center', borderRight: '1px solid #ddd', textTransform: 'none !important' }} data-testid="cn61-html-report-e55-e-format-header">E format</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center', borderRight: '1px solid #ddd', textTransform: 'none !important' }} data-testid="cn61-html-report-e55-mixed-mail-header">Mixed mail</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center', borderRight: '1px solid #ddd', textTransform: 'none !important' }} data-testid="cn61-html-report-e55-non-sampled-header">Non-sampled mail</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center', borderRight: '1px solid #ddd', textTransform: 'none !important' }} data-testid="cn61-html-report-e55-m-bags-header">M bags</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center', borderRight: '1px solid #ddd', textTransform: 'none !important' }} data-testid="cn61-html-report-e55-registered-items-header">Registered items</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center', borderRight: '1px solid #ddd', textTransform: 'none !important' }} data-testid="cn61-html-report-e55-insured-items-header">Insured items</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center', textTransform: 'none !important' }} data-testid="cn61-html-report-e55-express-items-header">Express items</TableCell>
              </TableRow>
            </TableHead>
            <TableBody data-testid={`cn61-html-report-e55-${sectionDirection}-table-body`}>
              {quarters.map((quarter, quarterIndex) => {
                const quarterData = e55Data[quarter];
                if (!quarterData || !quarterData.rows) return null;
                
                return quarterData.rows.map((row: any, rowIndex: number) => (
                  <TableRow key={`${quarter}-${rowIndex}`} sx={{ backgroundColor: getSectionBackgroundColor(false) }} data-testid={`cn61-html-report-e55-${sectionDirection}-table-row-${quarter}-${rowIndex}`}>
                    {/* Quarter cell with rowspan for first row of each quarter */}
                    {rowIndex === 0 && (
                      <TableCell 
                        rowSpan={quarterData.rows.length} 
                        sx={{ 
                          fontWeight: 600, 
                          borderRight: '1px solid #ddd', 
                          backgroundColor: getSectionBackgroundColor(false),
                          verticalAlign: 'middle',
                          textAlign: 'center'
                        }}
                        data-testid={`cn61-html-report-e55-${sectionDirection}-quarter-cell-${quarter}`}
                      >
                        Q{quarterIndex + 1}
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600, borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }} data-testid={`cn61-html-report-e55-${sectionDirection}-mail-category-cell-${quarter}-${rowIndex}`}>
                      {row.mailCategoryEnum}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }} data-testid={`cn61-html-report-e55-${sectionDirection}-p-or-s-format-cell-${quarter}-${rowIndex}`}>
                      {row.p_or_s_format_formatted || '0.0'}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }} data-testid={`cn61-html-report-e55-${sectionDirection}-g-format-cell-${quarter}-${rowIndex}`}>
                      {row.g_format_formatted || '0.0'}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }} data-testid={`cn61-html-report-e55-${sectionDirection}-e-format-cell-${quarter}-${rowIndex}`}>
                      {row.e_format_formatted || '0.0'}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }} data-testid={`cn61-html-report-e55-${sectionDirection}-mixed-mail-cell-${quarter}-${rowIndex}`}>
                      {row.mixedMail_formatted || '0.0'}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }} data-testid={`cn61-html-report-e55-${sectionDirection}-non-sampled-cell-${quarter}-${rowIndex}`}>
                      {row.nonSampled_formatted || '0.0'}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }} data-testid={`cn61-html-report-e55-${sectionDirection}-m-bags-cell-${quarter}-${rowIndex}`}>
                      {row.m_bags_formatted || '0.0'}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }} data-testid={`cn61-html-report-e55-${sectionDirection}-registered-items-cell-${quarter}-${rowIndex}`}>
                      {row.registeredItems_formatted || '0'}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }} data-testid={`cn61-html-report-e55-${sectionDirection}-insured-items-cell-${quarter}-${rowIndex}`}>
                      {row.insuredItems_formatted || '0'}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right', backgroundColor: getSectionBackgroundColor(false) }} data-testid={`cn61-html-report-e55-${sectionDirection}-express-items-cell-${quarter}-${rowIndex}`}>
                      {row.expressItems_formatted || '0'}
                    </TableCell>
                  </TableRow>
                ));
              })}
              
              {/* Summary Rows */}
              {e55Data.subTotal && (
                <TableRow sx={{ backgroundColor: getSectionBackgroundColor(false) }} data-testid={`cn61-html-report-e55-${sectionDirection}-subtotal-row`}>
                  <TableCell colSpan={2} sx={{ fontWeight: 600, borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }} data-testid={`cn61-html-report-e55-${sectionDirection}-subtotal-label`}>
                    Sub-Total
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.subTotal.p_or_s_format_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.subTotal.g_format_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.subTotal.e_format_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.subTotal.mixedMail_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.subTotal.nonSampled_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.subTotal.m_bags_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.subTotal.registeredItems_formatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.subTotal.insuredItems_formatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.subTotal.expressItems_formatted || '0'}
                  </TableCell>
                </TableRow>
              )}

              {/* Global Correction */}
              {e55Data.globalCorrection && (
                <TableRow sx={{ backgroundColor: getSectionBackgroundColor(false) }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 600, borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    Global Correction
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.globalCorrection.p_or_s_format_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.globalCorrection.g_format_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.globalCorrection.e_format_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.globalCorrection.mixedMail_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.globalCorrection.nonSampled_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.globalCorrection.m_bags_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.globalCorrection.registeredItems_formatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.globalCorrection.insuredItems_formatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.globalCorrection.expressItems_formatted || '0'}
                  </TableCell>
                </TableRow>
              )}

              {/* Mixed Mail Split from CN54bis */}
              {e55Data.formatMixedMail && (
                <TableRow sx={{ backgroundColor: getSectionBackgroundColor(false) }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 600, borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    Mixed Mail Split into Formats (from CN54bis)
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.formatMixedMail.p_or_s_format_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.formatMixedMail.g_format_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.formatMixedMail.e_format_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.formatMixedMail.mixedMail_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.formatMixedMail.nonSampled_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.formatMixedMail.m_bags_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.formatMixedMail.registeredItems_formatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.formatMixedMail.insuredItems_formatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.formatMixedMail.expressItems_formatted || '0'}
                  </TableCell>
                </TableRow>
              )}

              {/* IBRS Weight - Combined Row */}
              {(e55Data.ibrsWeightEFormat !== undefined || e55Data.ibrsWeightMixedMail !== undefined || 
                e55Data.ibrsWeightEFormat_formatted || e55Data.ibrsWeightMixedMail_formatted) && (
                <TableRow sx={{ backgroundColor: getSectionBackgroundColor(false) }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 600, borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    IBRS Weight
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: '#a7c0b4' }}>
                    {/* P or S Format - Empty */}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: '#a7c0b4' }}>
                    {/* G Format - Empty */}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.ibrsWeightEFormat_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.ibrsWeightMixedMail_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: '#a7c0b4' }}>
                    {/* Non Sampled - Empty */}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: '#a7c0b4' }}>
                    {/* M-Bags - Empty */}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: '#a7c0b4' }}>
                    {/* Registered Items - Empty */}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: '#a7c0b4' }}>
                    {/* Insured Items - Empty */}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', backgroundColor: '#a7c0b4' }}>
                    {/* Express Items - Empty */}
                  </TableCell>
                </TableRow>
              )}

              {/* UPU Mail - Combined Row */}
              {(e55Data.upuMailEFormat !== undefined || e55Data.upuMailMixedMail !== undefined || 
                e55Data.upuMailEFormat_formatted || e55Data.upuMailMixedMail_formatted) && (
                <TableRow sx={{ backgroundColor: getSectionBackgroundColor(false) }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 600, borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    UPU Mail
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: '#a7c0b4' }}>
                    {/* P or S Format - Empty */}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: '#a7c0b4' }}>
                    {/* G Format - Empty */}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.upuMailEFormat_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }}>
                    {e55Data.upuMailMixedMail_formatted || ''}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: '#a7c0b4' }}>
                    {/* Non Sampled - Empty */}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: '#a7c0b4' }}>
                    {/* M-Bags - Empty */}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: '#a7c0b4' }}>
                    {/* Registered Items - Empty */}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: '#a7c0b4' }}>
                    {/* Insured Items - Empty */}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', backgroundColor: '#a7c0b4' }}>
                    {/* Express Items - Empty */}
                  </TableCell>
                </TableRow>
              )}

              {/* Final Total */}
              {e55Data.total && (
                <TableRow sx={{ backgroundColor: getSectionBackgroundColor(true) }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 700, borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(true) }}>
                    Total
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 700, borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(true) }}>
                    {e55Data.total.p_or_s_format_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 700, borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(true) }}>
                    {e55Data.total.g_format_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 700, borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(true) }}>
                    {e55Data.total.e_format_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 700, borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(true) }}>
                    {e55Data.total.mixedMail_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 700, borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(true) }}>
                    {e55Data.total.nonSampled_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 700, borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(true) }}>
                    {e55Data.total.m_bags_formatted || '0.0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 700, borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(true) }}>
                    {e55Data.total.registeredItems_formatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 700, borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(true) }}>
                    {e55Data.total.insuredItems_formatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 700, backgroundColor: getSectionBackgroundColor(true) }}>
                    {e55Data.total.expressItems_formatted || '0'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // Render TD Calculations Sections with proper numbering
  const renderTdCalculationsSections = () => {
    const fromCountry = reportData.fromCountry || 'Origin';
    const toCountry = reportData.toCountry || 'Destination';
    
    return (
      <Box sx={{ mb: 4 }} data-testid="cn61-html-report-td-calculations-sections">
        {/* Main TD Calculations Title */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#063142', mb: 3 }} data-testid="cn61-html-report-td-calculations-title">
          2. Terminal dues calculations
        </Typography>
        
        {/* Inbound Section */}
        {(inboundData?.inboundTdCalculationsSection || reportData.inboundTdCalculationsSection) && 
          renderTdCalculationsSection(
            inboundData?.inboundTdCalculationsSection || reportData.inboundTdCalculationsSection, 
            `2.1 Inbound mail (from ${fromCountry} to ${toCountry})`, 
            'inbound'
          )}
        
        {/* Outbound Section */}
        {(outboundData?.outboundTdCalculationsSection || reportData.outboundTdCalculationsSection) && 
          renderTdCalculationsSection(
            outboundData?.outboundTdCalculationsSection || reportData.outboundTdCalculationsSection, 
            `2.2 Outbound mail (from ${toCountry} to ${fromCountry})`, 
            'outbound'
          )}
      </Box>
    );
  };

  // Render TD Calculations Section
  const renderTdCalculationsSection = (tdData: any, sectionTitle: string, sectionDirection: 'inbound' | 'outbound' = 'inbound') => {
    if (!tdData) return null;

    // Get currency from the data, default to EUR if not available
    const currency = reportData.currency || 'EUR';

    // Get background color for this specific section
    const getSectionBackgroundColor = (isHeader: boolean = false) => {
      if (isHeader) {
        return sectionDirection === 'inbound' ? '#e8f5e8' : sectionDirection === 'outbound' ? '#ffe8e8' : '#f5f5f5';
      } else {
        return sectionDirection === 'inbound' ? '#f0f8f0' : sectionDirection === 'outbound' ? '#fff0f0' : '#fafafa';
      }
    };

    // Convert the new data structure to ordered rows
    const getOrderedRows = () => {
      const rows = [];
      
      // Add rows in the correct order
      if (tdData.row_P_or_S_format) rows.push(tdData.row_P_or_S_format);
      if (tdData.row_G_format) rows.push(tdData.row_G_format);
      if (tdData.row_E_format) rows.push(tdData.row_E_format);
      if (tdData.row_MixedMail) rows.push(tdData.row_MixedMail);
      if (tdData.row_NonSampledMail) rows.push(tdData.row_NonSampledMail);
      if (tdData.row_M_bags) rows.push(tdData.row_M_bags);
      if (tdData.row_RegisteredItems) rows.push(tdData.row_RegisteredItems);
      if (tdData.row_InsuredItems) rows.push(tdData.row_InsuredItems);
      if (tdData.row_ExpressItems) rows.push(tdData.row_ExpressItems);
      if (tdData.row_Total) rows.push(tdData.row_Total);
      
      return rows;
    };

    const orderedRows = getOrderedRows();
    if (orderedRows.length === 0) return null;

    return (
      <Box sx={{ mb: 4 }} data-testid={`cn61-html-report-td-${sectionDirection}-section`}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#063142', mb: 2 }} data-testid={`cn61-html-report-td-${sectionDirection}-title`}>
          {sectionTitle}
        </Typography>
        <TableContainer component={Paper} sx={{ boxShadow: 1 }} data-testid={`cn61-html-report-td-${sectionDirection}-table-container`}>
          <Table size="small" data-testid={`cn61-html-report-td-${sectionDirection}-table`}>
            <TableHead data-testid={`cn61-html-report-td-${sectionDirection}-table-head`}>
              <TableRow sx={{ backgroundColor: getSectionBackgroundColor(true) }} data-testid={`cn61-html-report-td-${sectionDirection}-table-header-row`}>
                <TableCell sx={{ fontWeight: 700, borderRight: '1px solid #ddd' }} data-testid="cn61-html-report-td-row-type-header">Flow</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center', borderRight: '1px solid #ddd' }} data-testid="cn61-html-report-td-total-weight-header">Total Weight</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center', borderRight: '1px solid #ddd' }} data-testid="cn61-html-report-td-weight-rate-header">Weight Rate</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center', borderRight: '1px solid #ddd' }} data-testid="cn61-html-report-td-amount-weight-header">Amount for Weight [{currency}]</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center', borderRight: '1px solid #ddd' }} data-testid="cn61-html-report-td-ipk-header">IPK</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center', borderRight: '1px solid #ddd' }} data-testid="cn61-html-report-td-total-items-header">Total Items</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center', borderRight: '1px solid #ddd' }} data-testid="cn61-html-report-td-items-rate-header">Items Rate</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center' }} data-testid="cn61-html-report-td-amount-items-header">Amount for Items [{currency}]</TableCell>
              </TableRow>
            </TableHead>
            <TableBody data-testid={`cn61-html-report-td-${sectionDirection}-table-body`}>
              {orderedRows.map((row: any, index: number) => {
                const rowType = row.rowType;
                const isNonSampled = rowType === 'NON_SAMPLED_MAIL';
                const isMBags = rowType === 'M_BAGS';
                const isRegistered = rowType === 'REGISTERED_ITEMS';
                const isInsured = rowType === 'INSURED_ITEMS';
                const isExpress = rowType === 'EXPRESS_ITEMS';
                const isTotal = rowType === 'TOTAL';
                
                return (
                  <TableRow key={index} sx={{ backgroundColor: getSectionBackgroundColor(false) }} data-testid={`cn61-html-report-td-${sectionDirection}-table-row-${index}`}>
                    <TableCell sx={{ fontWeight: isTotal ? 700 : 600, borderRight: '1px solid #ddd', backgroundColor: getSectionBackgroundColor(false) }} data-testid={`cn61-html-report-td-${sectionDirection}-row-type-cell-${index}`}>
                      {formatRowTypeLabel(row.rowType)}
                    </TableCell>
                    {/* Total Weight */}
                    <TableCell sx={{ 
                      textAlign: 'right', 
                      fontWeight: isTotal ? 700 : 'normal',
                      borderRight: '1px solid #ddd', 
                      backgroundColor: (isRegistered || isInsured || isExpress || isTotal) ? '#a7c0b4' : getSectionBackgroundColor(false)
                    }}>
                      {(isRegistered || isInsured || isExpress || isTotal) ? '' : (row.totalWeight_formatted || '')}
                    </TableCell>
                    {/* Weight Rate */}
                    <TableCell sx={{ 
                      textAlign: 'right', 
                      fontWeight: isTotal ? 700 : 'normal',
                      borderRight: '1px solid #ddd', 
                      backgroundColor: (isRegistered || isInsured || isExpress || isTotal) ? '#a7c0b4' : getSectionBackgroundColor(false)
                    }}>
                      {(isRegistered || isInsured || isExpress || isTotal) ? '' : (row.weightRate_formatted || '')}
                    </TableCell>
                    {/* Amount for Weight */}
                    <TableCell sx={{ 
                      textAlign: 'right', 
                      fontWeight: isTotal ? 700 : 'normal',
                      borderRight: '1px solid #ddd', 
                      backgroundColor: (isRegistered || isInsured || isExpress) ? '#a7c0b4' : getSectionBackgroundColor(false)
                    }}>
                      {(isRegistered || isInsured || isExpress) ? '' : (row.amountForWeight_formatted || '')}
                    </TableCell>
                    {/* IPK */}
                    <TableCell sx={{ 
                      textAlign: 'right', 
                      fontWeight: isTotal ? 700 : 'normal',
                      borderRight: '1px solid #ddd', 
                      backgroundColor: (isNonSampled || isMBags || isRegistered || isInsured || isExpress || isTotal) ? '#a7c0b4' : getSectionBackgroundColor(false)
                    }}>
                      {(isNonSampled || isMBags || isRegistered || isInsured || isExpress || isTotal) ? '' : (row.ipk_formatted || '')}
                    </TableCell>
                    {/* Total Items */}
                    <TableCell sx={{ 
                      textAlign: 'right', 
                      fontWeight: isTotal ? 700 : 'normal',
                      borderRight: '1px solid #ddd', 
                      backgroundColor: (isMBags || isTotal) ? '#a7c0b4' : getSectionBackgroundColor(false)
                    }}>
                      {(isMBags || isTotal) ? '' : (row.totalItems_formatted || '')}
                    </TableCell>
                    {/* Items Rate */}
                    <TableCell sx={{ 
                      textAlign: 'right', 
                      fontWeight: isTotal ? 700 : 'normal',
                      borderRight: '1px solid #ddd', 
                      backgroundColor: (isMBags || isTotal) ? '#a7c0b4' : getSectionBackgroundColor(false)
                    }}>
                      {(isMBags || isTotal) ? '' : (row.itemsRate_formatted || '')}
                    </TableCell>
                    {/* Amount for Items */}
                    <TableCell sx={{ 
                      textAlign: 'right', 
                      fontWeight: isTotal ? 700 : 'normal',
                      backgroundColor: isMBags ? '#a7c0b4' : getSectionBackgroundColor(false)
                    }}>
                      {isMBags ? '' : (row.amountForItems_formatted || '')}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // Render Total Section
  const renderTotalSection = (totalData: any) => {
    if (!totalData) return null;

    // Get currency from the data, default to EUR if not available
    const currency = reportData.currency || 'EUR';

    return (
      <Box sx={{ mb: 4 }} data-testid="cn61-html-report-total-section">
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#063142', mb: 2 }} data-testid="cn61-html-report-total-title">
          Summary
        </Typography>
        <TableContainer component={Paper} sx={{ boxShadow: 1 }} data-testid="cn61-html-report-total-table-container">
          <Table size="small" data-testid="cn61-html-report-total-table">
            <TableHead data-testid="cn61-html-report-total-table-head">
              <TableRow sx={{ backgroundColor: '#f5f5f5' }} data-testid="cn61-html-report-total-table-header-row">
                <TableCell sx={{ fontWeight: 700, borderRight: '1px solid #ddd' }} data-testid="cn61-html-report-total-description-header"></TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center', borderRight: '1px solid #ddd' }} data-testid="cn61-html-report-total-amount-weight-header">Amount for Weight [{currency}]</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center', borderRight: '1px solid #ddd' }} data-testid="cn61-html-report-total-amount-items-header">Amount for Items [{currency}]</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center' }} data-testid="cn61-html-report-total-total-header">Total [{currency}]</TableCell>
              </TableRow>
            </TableHead>
            <TableBody data-testid="cn61-html-report-total-table-body">
              <TableRow sx={{ backgroundColor: '#f0f8f0' }} data-testid="cn61-html-report-total-inbound-row">
                <TableCell sx={{ fontWeight: 600, borderRight: '1px solid #ddd', backgroundColor: '#f0f8f0' }} data-testid="cn61-html-report-total-inbound-description">
                  Inbound mail (table 2.1), from {reportData.fromCountry} to {reportData.toCountry}
                </TableCell>
                <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: '#f0f8f0' }} data-testid="cn61-html-report-total-inbound-amount-weight">
                  {totalData.inboundAmountForWeight_formatted || '0.00'}
                </TableCell>
                <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: '#f0f8f0' }} data-testid="cn61-html-report-total-inbound-amount-items">
                  {totalData.inboundAmountForItems_formatted || '0.00'}
                </TableCell>
                <TableCell sx={{ textAlign: 'right', fontWeight: 600, backgroundColor: '#f0f8f0' }} data-testid="cn61-html-report-total-inbound-total">
                  {totalData.inboundTotal_formatted || '0.00'}
                </TableCell>
              </TableRow>
              <TableRow sx={{ backgroundColor: '#fff0f0' }} data-testid="cn61-html-report-total-outbound-row">
                <TableCell sx={{ fontWeight: 600, borderRight: '1px solid #ddd', backgroundColor: '#fff0f0' }} data-testid="cn61-html-report-total-outbound-description">
                  Outbound mail (table 2.2), from {reportData.toCountry} to {reportData.fromCountry}
                </TableCell>
                <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: '#fff0f0' }} data-testid="cn61-html-report-total-outbound-amount-weight">
                  {totalData.outboundAmountForWeight_formatted || '0.00'}
                </TableCell>
                <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: '#fff0f0' }} data-testid="cn61-html-report-total-outbound-amount-items">
                  {totalData.outboundAmountForItems_formatted || '0.00'}
                </TableCell>
                <TableCell sx={{ textAlign: 'right', fontWeight: 600, backgroundColor: '#fff0f0' }} data-testid="cn61-html-report-total-outbound-total">
                  {totalData.outboundTotal_formatted || '0.00'}
                </TableCell>
              </TableRow>
              <TableRow sx={{ backgroundColor: '#e8f5e8' }} data-testid="cn61-html-report-total-grand-total-row">
                <TableCell sx={{ fontWeight: 700, borderRight: '1px solid #ddd', backgroundColor: '#e8f5e8' }} data-testid="cn61-html-report-total-grand-total-description">
                  Amount to be received by {reportData.toCountry} from {reportData.fromCountry}
                </TableCell>
                <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: '#e8f5e8' }} data-testid="cn61-html-report-total-grand-total-amount-weight">
                  {/* Empty for grand total - no weight total */}
                </TableCell>
                <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: '#e8f5e8' }} data-testid="cn61-html-report-total-grand-total-amount-items">
                  {/* Empty for grand total - no items total */}
                </TableCell>
                <TableCell sx={{ 
                  textAlign: 'right', 
                  fontWeight: 700, 
                  backgroundColor: '#e8f5e8',
                  color: totalData.grandTotal < 0 ? '#c62828' : '#2e7d32'
                }} data-testid="cn61-html-report-total-grand-total">
                  {totalData.grandTotal_formatted || '0.00'}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // Check if we have any data to render - handle both new nested structure and legacy structure
  // Handle the case where inbound/outbound are arrays
  const inboundData = Array.isArray(reportData.inbound) ? reportData.inbound[0] : reportData.inbound;
  const outboundData = Array.isArray(reportData.outbound) ? reportData.outbound[0] : reportData.outbound;
  
  const hasE55Data = 
    inboundData?.inboundE55SummarySection || 
    outboundData?.outboundE55SummarySection ||
    reportData.inboundE55SummarySection || 
    reportData.outboundE55SummarySection;
  
  const hasTdCalculationsData = 
    (inboundData?.inboundTdCalculationsSection && Object.keys(inboundData.inboundTdCalculationsSection).length > 0) || 
    (outboundData?.outboundTdCalculationsSection && Object.keys(outboundData.outboundTdCalculationsSection).length > 0) ||
    (reportData.inboundTdCalculationsSection && Object.keys(reportData.inboundTdCalculationsSection).length > 0) || 
    (reportData.outboundTdCalculationsSection && Object.keys(reportData.outboundTdCalculationsSection).length > 0);

  const hasTotalData = reportData.totalSection;

  // If no data at all, show message
  if (!hasE55Data && !hasTdCalculationsData && !hasTotalData) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }} data-testid="cn61-html-report-no-data">
        <Typography variant="body1" color="text.secondary" data-testid="cn61-html-report-no-data-text">
          No report data available to display
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }} data-testid="cn61-html-report-table-container">
      {/* Report Header */}
      <Box sx={{ mb: 3 }} data-testid="cn61-html-report-header">
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 600, 
            color: direction === 'inbound' ? '#2e7d32' : direction === 'outbound' ? '#c62828' : '#063142',
            mb: 1,
            p: 2,
            backgroundColor: getBackgroundColor(true),
            borderRadius: 1,
            textAlign: 'center'
          }} 
          data-testid="cn61-html-report-title"
        >
          {title || 'CN61 UPU Terminal Dues Report'}
        </Typography>
      </Box>

      {/* E55 Summary Sections */}
      {((inboundData?.inboundE55SummarySection || reportData.inboundE55SummarySection) || 
        (outboundData?.outboundE55SummarySection || reportData.outboundE55SummarySection)) && 
        renderE55SummarySections()}

      {/* TD Calculations Sections */}
      {((inboundData?.inboundTdCalculationsSection || reportData.inboundTdCalculationsSection) || 
        (outboundData?.outboundTdCalculationsSection || reportData.outboundTdCalculationsSection)) && 
        renderTdCalculationsSections()}

      {/* Total Summary Section */}
      {hasTotalData && renderTotalSection(reportData.totalSection)}
    </Box>
  );
}

export default CN61HtmlReportTable;
